import { API_BASE_URL } from '../config/api';

export const voiceSocketUrl = path => {
  const base = new URL(API_BASE_URL);
  const url = new URL(path, base);
  if (url.origin !== base.origin || url.search || url.hash || url.username || url.password) throw new Error('Invalid voice relay address.');
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.href;
};

export class ExaminerVoice {
  constructor(onEvent, onFailure, onPlaybackLevel = () => {}) {
    this.onEvent = onEvent;
    this.onFailure = onFailure;
    this.onPlaybackLevel = onPlaybackLevel;
    this.sources = new Set();
    this.closed = false;
    this.muted = false;
    this.nextTime = 0;
  }

  async prepare() {
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.AudioContext || !window.AudioWorkletNode) {
        throw new Error('Voice requires a supported browser with HTTPS, microphone access and AudioWorklet.');
      }
      this.context = new window.AudioContext();
      await this.context.resume();
      if (this.closed) { await this.context.close(); return; }
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (this.closed) { this.stream.getTracks().forEach(track => track.stop()); return; }
      this.stream.getTracks().forEach(track => { track.onended = () => this.fail('Microphone access ended. Reconnect to continue.'); });
      await this.context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/examiner-capture.js`);
      if (this.closed) return;
      this.worklet = new window.AudioWorkletNode(this.context, 'examiner-capture');
      this.input = this.context.createMediaStreamSource(this.stream);
      this.silent = this.context.createGain();
      this.silent.gain.value = 0;
      this.output = this.context.createGain();
      this.analyser = this.context.createAnalyser();
      this.analyser.fftSize = 256;
      this.samples = new Float32Array(this.analyser.fftSize);
      this.output.connect(this.analyser);
      this.analyser.connect(this.context.destination);
      this.worklet.port.onmessage = ({ data }) => {
        if (this.closed || !this.ready || this.muted) return;
        if (this.socket.bufferedAmount > 64000) { this.fail('Connection cannot keep up with microphone audio. Reconnect when the network improves.'); return; }
        const bytes = new Uint8Array(data);
        this.send({ type: 'input_audio_buffer.append', audio: btoa(String.fromCharCode(...bytes)) });
      };
      this.context.onstatechange = () => {
        if (!this.closed && this.ready && this.context.state !== 'running') this.fail('Browser audio was suspended. Reconnect to continue.');
      };
    } catch (failure) {
      this.stop();
      throw failure;
    }
  }

  connect(ticket) {
    if (this.closed) return;
    this.socket = new WebSocket(voiceSocketUrl(ticket.websocketPath));
    this.timeout = window.setTimeout(() => this.fail('Voice connection timed out. Request a new connection ticket.'), 20000);
    this.socket.onopen = () => { this.send({ type: 'authenticate', ticket: ticket.ticket }); ticket = null; };
    this.socket.onmessage = ({ data }) => {
      if (this.closed) return;
      try {
        const event = JSON.parse(data);
        if (event.type === 'examiner.ready') {
          if (this.ready) return;
          if (!Number.isFinite(Date.parse(event.deadline))) throw new Error('Voice relay returned an invalid deadline.');
          if (this.context.state !== 'running') throw new Error('Browser audio is not running. Reconnect to continue.');
          clearTimeout(this.timeout);
          this.ready = true;
          this.input.connect(this.worklet);
          this.worklet.connect(this.silent);
          this.silent.connect(this.context.destination);
          this.deadlineTimer = window.setTimeout(() => this.fail('Examination time allowance has ended.'), Math.max(0, Date.parse(event.deadline) - Date.now()));
        } else if (event.type === 'response.output_audio.delta') this.play(event.delta);
        else if (event.type === 'input_audio_buffer.speech_started') this.clearPlayback();
        else if (event.type === 'examiner.closed') { this.fail(event.message || 'Voice connection ended.'); return; }
        this.onEvent(event);
      } catch (failure) { this.fail(failure.message || 'Invalid voice relay event.'); }
    };
    this.socket.onerror = () => this.fail('Voice connection failed. Check your connection and deployment origin settings, then reconnect.');
    this.socket.onclose = () => this.fail('Voice disconnected. Reconnect using the same examination; its allowance does not reset.');
  }

  send(event) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try { this.socket.send(JSON.stringify(event)); }
      catch { this.fail('Unable to send voice data. Reconnect when the connection is available.'); }
    }
  }

  play(encoded) {
    if (!this.ready) throw new Error('Received audio before voice was ready.');
    if (typeof encoded !== 'string' || encoded.length > 32768) throw new Error('Invalid examiner audio frame.');
    const bytes = Uint8Array.from(atob(encoded), char => char.charCodeAt(0));
    if (bytes.length % 2) throw new Error('Invalid examiner audio.');
    const duration = bytes.length / 48000;
    if (Math.max(0, this.nextTime - this.context.currentTime) + duration > 20 || this.sources.size > 500) {
      throw new Error('Examiner playback queue exceeded its limit. Reconnect to continue.');
    }
    const buffer = this.context.createBuffer(1, bytes.length / 2, 24000);
    const values = buffer.getChannelData(0);
    const view = new DataView(bytes.buffer);
    for (let i = 0; i < values.length; i += 1) values[i] = view.getInt16(i * 2, true) / 32768;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.output);
    this.sources.add(source);
    source.onended = () => { source.disconnect(); this.sources.delete(source); };
    this.nextTime = Math.max(this.context.currentTime, this.nextTime);
    source.start(this.nextTime);
    this.nextTime += duration;
    this.monitorPlayback();
  }

  monitorPlayback() {
    if (this.animationFrame != null) return;
    let previousTime = 0;
    const sample = time => {
      this.animationFrame = null;
      if (this.closed || !this.sources.size) { this.onPlaybackLevel(0); return; }
      if (time - previousTime >= 33) {
        this.analyser.getFloatTimeDomainData(this.samples);
        const energy = this.samples.reduce((sum, value) => sum + value * value, 0) / this.samples.length;
        this.onPlaybackLevel(this.output.gain.value ? Math.min(1, Math.sqrt(energy) * 5) : 0);
        previousTime = time;
      }
      this.animationFrame = requestAnimationFrame(sample);
    };
    this.animationFrame = requestAnimationFrame(sample);
  }

  clearPlayback() {
    if (this.animationFrame != null) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    for (const source of this.sources) { source.stop(); source.disconnect(); }
    this.sources.clear();
    this.nextTime = 0;
    this.onPlaybackLevel(0);
  }

  cancel() { this.send({ type: 'response.cancel' }); this.clearPlayback(); }
  mute(value) {
    this.muted = value;
    this.stream?.getAudioTracks().forEach(track => { track.enabled = !value; });
    this.send({ type: 'input_audio_buffer.clear' });
  }
  volume(value) {
    if (this.output) this.output.gain.value = value;
    if (!value) this.onPlaybackLevel(0);
  }
  fail(message) {
    if (this.closed) return;
    this.stop();
    this.onFailure(new Error(message));
  }
  stop() {
    this.closed = true;
    this.ready = false;
    clearTimeout(this.timeout);
    clearTimeout(this.deadlineTimer);
    this.send({ type: 'examiner.stop' });
    if (this.socket) {
      this.socket.onopen = this.socket.onmessage = this.socket.onerror = this.socket.onclose = null;
      this.socket.close();
    }
    this.stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
    if (this.worklet) { this.worklet.port.onmessage = null; this.worklet.disconnect(); this.worklet.port.close(); }
    this.input?.disconnect();
    this.silent?.disconnect();
    this.clearPlayback();
    this.output?.disconnect();
    this.analyser?.disconnect();
    if (this.context && this.context.state !== 'closed') {
      this.context.onstatechange = null;
      this.context.close().catch(() => this.onFailure(new Error('Unable to close browser audio. Close this tab to release it.')));
    }
  }
}
